/**
 * Compatibility surface over `@voice/audioBus`.
 *
 * This layer keeps the callers that predate the bus working EXACTLY as before,
 * error contract included. The bus resolves and records a failure in its state;
 * these functions REJECT, because callers branch on a rejection to fall back to
 * speech synthesis, to show a voice-unavailable banner, and to withhold the
 * dictation answer gate. Silently converting those rejections into resolutions
 * turns each of them into a silent failure, which is the bug this project exists
 * to remove. New code calls the bus directly and reads its state instead.
 *
 * One sound source is still OUTSIDE the bus: speech synthesis, which has no
 * element for the bus to own. It is tracked here so the tutor pill and
 * exclusivity keep covering it. The passage element was the other one, until
 * Task 8 moved PassageReader onto the bus and this file stopped tracking it.
 */
import { speechSynthesisRate } from "@lib/lessonPreferences";
import {
  getAudioState,
  playAudio,
  stopAudio,
  subscribeAudio,
  type AudioOutcome,
} from "@voice/audioBus";

export const SPEECH_START_TIMEOUT_MS = 3_000;

const playListeners = new Set<() => void>();
const stopListeners = new Set<() => void>();

let busSounding = false;
let speechSounding = false;
let sounding = false;

/** One notification per real transition, whichever of the two sources caused it. */
function recompute() {
  const next = busSounding || speechSounding;
  if (next === sounding) return;
  sounding = next;
  for (const listener of next ? playListeners : stopListeners) listener();
}

/**
 * SOUNDING, not "the bus holds a clip". A paused clip makes no sound, so a pause
 * reports false here and fires the stop notification - which is what the tutor
 * pill wants, since its waveform must freeze while paused.
 *
 * Subscribers that track OWNERSHIP of a clip rather than audibility must not
 * treat that notification as the clip being gone: check `getAudioState()` for
 * `"paused"` and stay put, as PassageReader and useTutorAudioPlayback do. Widening
 * this flag to cover `"paused"` instead would fix them and break the pill.
 */
subscribeAudio((state) => {
  busSounding = state.status === "playing";
  recompute();
});

export function subscribeLessonAudioPlay(listener: () => void): () => void {
  playListeners.add(listener);
  return () => {
    playListeners.delete(listener);
  };
}

export function subscribeLessonAudioStop(listener: () => void): () => void {
  stopListeners.add(listener);
  return () => {
    stopListeners.delete(listener);
  };
}

/** Current aggregate sounding state, for consumers that mount mid-playback. */
export function isLessonAudioSounding(): boolean {
  return sounding;
}

export function stopLessonAudio(): void {
  // The non-bus source has genuinely been silenced here, so clear it rather than
  // waiting on a speechSynthesis `end` that may arrive late or never. A flag stuck
  // true suppresses ALL future play/stop notifications for the life of the page,
  // because this is module singleton state. The pre-bus module notified stop
  // unconditionally for this reason.
  speechSounding = false;
  stopAudio();
  recompute();
}

/**
 * The bus names why it settled; these callers expect a throw. Translate - and ONLY
 * for the outcome that means a fault.
 *
 * The check this replaces was `getAudioState().status === "error"`, which asks the
 * bus a question about WHATEVER clip it happens to be holding. Under supersession
 * that is somebody else's clip, so a lesson clip could be blamed for an activity's
 * blocked autoplay and vice versa. The outcome is per-clip and cannot be wrong about
 * whose it is. A stopped or superseded clip is not a failure and must not raise the
 * voice-unavailable banner or withhold the dictation answer gate.
 *
 * The REASON still comes from state, deliberately: the outcome says whether this clip
 * failed, state says which kind, and it is only asked once the outcome has said there
 * was a failure at all. That is one question each, not the same question twice.
 */
function throwIfFailed(outcome: AudioOutcome): void {
  if (outcome !== "failed") return;
  const state = getAudioState();
  if (state.status === "error" && state.reason === "blocked") {
    throw new DOMException("Autoplay blocked", "NotAllowedError");
  }
  throw new Error("Audio playback failed");
}

export async function playLessonAudioUrl(url: string, playbackRate = 1): Promise<void> {
  stopLessonAudio();
  const outcome = await playAudio({
    source: { kind: "url", url },
    intent: "lesson",
    label: "audio.lessonAudio",
    rate: playbackRate,
  });
  throwIfFailed(outcome);
}

export async function playLessonAudioBuffer(buffer: ArrayBuffer, playbackRate = 1): Promise<void> {
  stopLessonAudio();
  const outcome = await playAudio({
    source: { kind: "buffer", buffer },
    intent: "lesson",
    label: "audio.lessonAudio",
    rate: playbackRate,
  });
  throwIfFailed(outcome);
}

/**
 * Speech synthesis has no element for the bus to own, so it stays here and reports
 * its own sounding state. The bus is stopped first so a spoken fallback cannot
 * overlap a clip.
 */
export async function playLessonSpeechSynthesis(text: string, tutorSpeechSpeed = 1): Promise<void> {
  if (typeof window === "undefined" || !window.speechSynthesis) return;

  stopLessonAudio();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = speechSynthesisRate(tutorSpeechSpeed);

  let startWatchdog: ReturnType<typeof setTimeout> | undefined;
  try {
    await new Promise<void>((resolve) => {
      startWatchdog = setTimeout(() => {
        window.speechSynthesis.cancel();
        resolve();
      }, SPEECH_START_TIMEOUT_MS);
      utterance.onstart = () => {
        clearTimeout(startWatchdog);
        speechSounding = true;
        recompute();
      };
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      window.speechSynthesis.speak(utterance);
    });
  } finally {
    clearTimeout(startWatchdog);
    speechSounding = false;
    recompute();
  }
}
